import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import api from '../services/api';
import './GroupForm.css';

const GroupForm = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, watch, setValue, reset, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      discipline_id: '',
      level_id: '',
      teacher_id: '',
      location_id: '',
      capacity: 20,
    },
  });

  const EMPTY_ROW = () => ({ day_of_week: 0, start_time: '', end_time: '', is_active: true });
  const [scheduleRows, setScheduleRows] = useState([]);

  const selectedDisciplineId = watch('discipline_id');
  const selectedLevelId = watch('level_id');
  
  // Usar ref para rastrear si estamos en modo edición y el nivel inicial
  const isEditingMode = useRef(!!initialData);
  const initialLevelIdRef = useRef(null);
  
  // Actualizar refs cuando initialData cambia
  useEffect(() => {
    isEditingMode.current = !!initialData;
    if (initialData) {
      initialLevelIdRef.current = initialData.level_id || (initialData.level?.id) || null;
    } else {
      initialLevelIdRef.current = null;
    }
  }, [initialData]);
  
  // Convertir NaN a string vacío para evitar problemas de renderizado
  const safeDisciplineId = selectedDisciplineId && !isNaN(selectedDisciplineId) ? selectedDisciplineId : '';
  const safeLevelId = selectedLevelId && !isNaN(selectedLevelId) ? selectedLevelId : '';

  // Obtener disciplinas
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  // Obtener niveles de la disciplina seleccionada, ordenados por order
  // Filtrar para mostrar solo niveles utilizables (subniveles y niveles de habilidad independientes)
  const { data: levels, isLoading: levelsLoading } = useQuery(
    ['levels', safeDisciplineId],
    async () => {
      if (!safeDisciplineId || safeDisciplineId === '' || isNaN(safeDisciplineId)) return [];
      const response = await api.get('/levels', {
        params: { discipline_id: safeDisciplineId, is_active: true }
      });
      
      // Filtrar niveles utilizables para grupos:
      // 1. Subniveles de habilidad (tienen parent_level_id y category_type = 'skill')
      // 2. Niveles de habilidad independientes (sin parent_level_id y category_type = 'skill')
      // NO mostrar categorías por edad (category_type = 'age' sin parent_level_id)
      const usableLevels = (response.data || []).filter(level => {
        // Solo niveles activos
        if (!level.is_active) return false;
        
        // Si es subnivel (tiene parent), incluirlo
        if (level.parent_level_id) return true;
        
        // Si es nivel independiente de habilidad, incluirlo
        if (level.category_type === 'skill' && !level.parent_level_id) return true;
        
        // Excluir categorías por edad (son solo contenedores)
        return false;
      });
      
      // Ordenar: primero por categoría padre (nombre), luego por order dentro de cada categoría
      const sorted = usableLevels.sort((a, b) => {
        const parentA = a.parent?.name || '';
        const parentB = b.parent?.name || '';
        if (parentA !== parentB) return parentA.localeCompare(parentB, 'es');
        return (a.order || 0) - (b.order || 0);
      });
      
      // Formatear nombres y etiqueta de edad
      return sorted.map(level => {
        let displayName = level.name;
        let ageLabel = '';

        // Si es subnivel, la edad viene del padre
        if (level.parent_level_id && level.parent) {
          displayName = `${level.parent.name} → ${level.name}`;
          const ageParts = [];
          if (level.parent.min_age || level.parent.max_age) {
            ageParts.push(
              level.parent.min_age === level.parent.max_age
                ? `${level.parent.min_age} años`
                : `${level.parent.min_age || '?'}-${level.parent.max_age || '?'} años`
            );
          }
          if (level.parent.min_birth_year || level.parent.max_birth_year) {
            ageParts.push(
              level.parent.min_birth_year === level.parent.max_birth_year
                ? `Año ${level.parent.min_birth_year}`
                : `Año ${level.parent.min_birth_year || '?'}-${level.parent.max_birth_year || '?'}`
            );
          }
          ageLabel = ageParts.join(' · ');
        } else {
          // Nivel independiente: usar sus propios campos de edad
          if (level.min_age || level.max_age) {
            ageLabel = level.min_age === level.max_age
              ? `${level.min_age} años`
              : `${level.min_age || '?'}-${level.max_age || '?'} años`;
          } else if (level.min_birth_year || level.max_birth_year) {
            ageLabel = level.min_birth_year === level.max_birth_year
              ? `Año ${level.min_birth_year}`
              : `Año ${level.min_birth_year || '?'}-${level.max_birth_year || '?'}`;
          }
        }

        return { ...level, displayName, ageLabel };
      });
    },
    { 
      enabled: !!safeDisciplineId && safeDisciplineId !== '' && !isNaN(safeDisciplineId),
      onSuccess: (data) => {
        // En modo edición, asegurar que el level_id se establezca cuando los niveles se carguen
        if (isEditingMode.current && initialLevelIdRef.current && data.length > 0) {
          // Verificar que el nivel inicial existe en los niveles cargados
          const levelExists = data.some(level => level.id === initialLevelIdRef.current);
          if (levelExists) {
            // Asegurar que el valor esté establecido (puede haberse perdido durante la carga)
            const currentLevelId = watch('level_id');
            if (!currentLevelId || currentLevelId !== initialLevelIdRef.current) {
              setValue('level_id', initialLevelIdRef.current, { shouldValidate: false });
            }
          }
        }
        
        // Solo resetear el nivel en modo creación cuando cambia la disciplina
        if (!isEditingMode.current) {
          // Modo creación: resetear el nivel cuando cambia la disciplina
          if (safeLevelId && data.length > 0) {
            const currentLevelExists = data.some(level => level.id === parseInt(safeLevelId));
            if (!currentLevelExists) {
              setValue('level_id', '');
            }
          } else if (safeLevelId && data.length === 0) {
            setValue('level_id', '');
          }
        }
      }
    }
  );

  // Obtener solo profesores
  const { data: users } = useQuery('teachers', async () => {
    const response = await api.get('/users', { params: { role: 'teacher' } });
    return response.data;
  });

  // Obtener sedes
  const { data: locations } = useQuery('locations', async () => {
    const response = await api.get('/locations');
    return response.data;
  });

  // Actualizar valores del formulario cuando initialData cambie o esté disponible
  useEffect(() => {
    if (initialData) {
      // Manejar tanto IDs directos como objetos anidados (ej: initialData.level.id)
      const getNestedId = (obj, directKey, nestedKey) => {
        if (obj[directKey] !== undefined && obj[directKey] !== null) {
          return Number(obj[directKey]);
        }
        if (obj[nestedKey] && obj[nestedKey].id) {
          return Number(obj[nestedKey].id);
        }
        return '';
      };

      const disciplineId = getNestedId(initialData, 'discipline_id', 'discipline') || '';
      const levelId = getNestedId(initialData, 'level_id', 'level') || '';

      // Usar reset para establecer todos los valores del formulario cuando initialData esté disponible
      reset({
        name: initialData.name || '',
        discipline_id: disciplineId,
        level_id: levelId,
        teacher_id: getNestedId(initialData, 'teacher_id', 'teacher') || '',
        location_id: getNestedId(initialData, 'location_id', 'location') || '',
        capacity: initialData.capacity ? Number(initialData.capacity) : 20,
      });

      // Load schedule rows from initialData.schedules
      const rows = (initialData.schedules || []).map(s => ({
        day_of_week: s.day_of_week ?? 0,
        start_time: s.start_time ? s.start_time.slice(0, 5) : '',
        end_time: s.end_time ? s.end_time.slice(0, 5) : '',
        is_active: s.is_active !== undefined ? s.is_active : true,
      }));
      setScheduleRows(rows);
    } else {
      reset({
        name: '',
        discipline_id: '',
        level_id: '',
        teacher_id: '',
        location_id: '',
        capacity: 20,
      });
      setScheduleRows([]);
    }
  }, [initialData, reset]);

  // Asegurar que el level_id se establezca cuando los niveles se carguen en modo edición
  // Este useEffect se ejecuta cuando los niveles están disponibles
  useEffect(() => {
    if (initialData && initialLevelIdRef.current && levels && levels.length > 0 && !levelsLoading) {
      const levelExists = levels.some(level => level.id === initialLevelIdRef.current);
      if (levelExists) {
        const currentLevelId = watch('level_id');
        // Establecer el level_id solo si no está ya establecido correctamente
        if (!currentLevelId || currentLevelId === '' || Number(currentLevelId) !== Number(initialLevelIdRef.current)) {
          setValue('level_id', initialLevelIdRef.current, { shouldValidate: false, shouldDirty: false });
        }
      }
    }
  }, [levels, levelsLoading, initialData, setValue, watch]);

  const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  const addScheduleRow = () => setScheduleRows(prev => [...prev, EMPTY_ROW()]);

  const removeScheduleRow = (idx) =>
    setScheduleRows(prev => prev.filter((_, i) => i !== idx));

  const updateScheduleRow = (idx, field, value) =>
    setScheduleRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));

  const handleFormSubmit = (data) => {
    // Validate inline schedule rows
    for (let i = 0; i < scheduleRows.length; i++) {
      const row = scheduleRows[i];
      if (!row.start_time || !row.end_time) {
        setError('schedules', { type: 'manual', message: `Fila ${i + 1}: completa hora de inicio y fin` });
        return;
      }
      if (row.end_time <= row.start_time) {
        setError('schedules', { type: 'manual', message: `Fila ${i + 1}: la hora de fin debe ser mayor a la de inicio` });
        return;
      }
    }
    // Check overlaps within same day
    for (let i = 0; i < scheduleRows.length; i++) {
      for (let j = i + 1; j < scheduleRows.length; j++) {
        const a = scheduleRows[i], b = scheduleRows[j];
        if (a.day_of_week === b.day_of_week && a.start_time < b.end_time && a.end_time > b.start_time) {
          setError('schedules', { type: 'manual', message: `Solapamiento en ${DAY_NAMES[a.day_of_week]}: filas ${i + 1} y ${j + 1}` });
          return;
        }
      }
    }
    clearErrors('schedules');
    onSubmit({ ...data, schedules: scheduleRows });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="group-form">
      <div className="form-group">
        <label htmlFor="name">Nombre del Grupo *</label>
        <input
          type="text"
          id="name"
          {...register('name', { required: 'El nombre del grupo es obligatorio' })}
          className={errors.name ? 'input-error' : ''}
          placeholder="Ej: Natación Nivel 1 - Grupo A"
        />
        {errors.name && <span className="error-message">{errors.name.message}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="discipline_id">Disciplina *</label>
          <select
            id="discipline_id"
            {...register('discipline_id', { 
              required: 'La disciplina es obligatoria',
              setValueAs: (value) => {
                if (value === '' || value === null || value === undefined || isNaN(value)) {
                  return '';
                }
                return parseInt(value);
              }
            })}
            className={errors.discipline_id ? 'input-error' : ''}
          >
            <option value="">Seleccionar disciplina...</option>
            {disciplines?.map((discipline) => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.icon || '⚽'} {discipline.name}
              </option>
            ))}
          </select>
          {errors.discipline_id && <span className="error-message">{errors.discipline_id.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="level_id">Nivel *</label>
          <select
            id="level_id"
            {...register('level_id', {
              required: 'El nivel es obligatorio',
              setValueAs: (value) => {
                if (value === '' || value === null || value === undefined || isNaN(value)) return '';
                return parseInt(value);
              }
            })}
            disabled={!safeDisciplineId || safeDisciplineId === '' || levelsLoading}
            className={errors.level_id ? 'input-error' : ''}
          >
            <option value="">
              {!safeDisciplineId || safeDisciplineId === ''
                ? 'Selecciona primero una disciplina'
                : levelsLoading
                ? 'Cargando niveles...'
                : 'Seleccionar nivel...'}
            </option>
            {levels?.map(level => (
              <option key={level.id} value={level.id}>
                {level.displayName}{level.ageLabel ? ` (${level.ageLabel})` : ''}
              </option>
            ))}
          </select>
          {!safeDisciplineId || safeDisciplineId === '' ? null : !levelsLoading && (!levels || levels.length === 0) ? (
            <span className="info-message">⚠️ Sin niveles. Créalos primero en la sección Niveles.</span>
          ) : null}
          {errors.level_id && <span className="error-message">{errors.level_id.message}</span>}
        </div>
      </div>

      <div className="form-group">
        <label>
          Horarios
          {scheduleRows.length > 0 && (
            <span className="schedule-selected-count">{scheduleRows.length} {scheduleRows.length === 1 ? 'franja' : 'franjas'}</span>
          )}
        </label>
        <div className={`schedule-rows-container${errors.schedules ? ' rows-error' : ''}`}>
          {scheduleRows.length === 0 && (
            <div className="schedule-rows-empty">Sin horarios asignados. Agrega al menos uno.</div>
          )}
          {scheduleRows.map((row, idx) => (
            <div key={idx} className="schedule-row">
              <select
                className="schedule-row-day"
                value={row.day_of_week}
                onChange={e => updateScheduleRow(idx, 'day_of_week', Number(e.target.value))}
              >
                {DAY_NAMES.map((d, i) => (
                  <option key={i} value={i}>{d}</option>
                ))}
              </select>
              <input
                type="time"
                className="schedule-row-time"
                value={row.start_time}
                onChange={e => updateScheduleRow(idx, 'start_time', e.target.value)}
              />
              <span className="schedule-row-sep">–</span>
              <input
                type="time"
                className="schedule-row-time"
                value={row.end_time}
                onChange={e => updateScheduleRow(idx, 'end_time', e.target.value)}
              />
              <button
                type="button"
                className="schedule-row-remove"
                onClick={() => removeScheduleRow(idx)}
                title="Eliminar franja"
              >×</button>
            </div>
          ))}
          <button type="button" className="schedule-row-add" onClick={addScheduleRow}>
            + Agregar franja horaria
          </button>
        </div>
        {errors.schedules && <span className="error-message">{errors.schedules.message}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="teacher_id">Profesor</label>
          <select
            id="teacher_id"
            {...register('teacher_id', { 
              setValueAs: (value) => {
                if (value === '' || value === null || value === undefined || isNaN(value)) {
                  return null;
                }
                return parseInt(value);
              }
            })}
          >
            <option value="">Sin asignar</option>
            {users?.filter(user => {
              return user.roles && user.roles.some(role => role.name === 'teacher');
            }).map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name} ({user.username})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="location_id">Sede</label>
          <select
            id="location_id"
            {...register('location_id', { 
              setValueAs: (value) => {
                if (value === '' || value === null || value === undefined || isNaN(value)) {
                  return null;
                }
                return parseInt(value);
              }
            })}
          >
            <option value="">Seleccionar sede...</option>
            {locations?.filter(location => location.is_active).map((location) => (
              <option key={location.id} value={location.id}>
                {location.name} {location.code ? `(${location.code})` : ''}
              </option>
            ))}
          </select>
          <span className="info-message">
            Sin sede → se asigna "Sede 1" automáticamente
          </span>
        </div>
      </div>

      <div className="form-group form-group-capacity">
        <label htmlFor="capacity">Capacidad Máxima *</label>
        <div className="capacity-input-row">
          <input
            type="number"
            id="capacity"
            min="1"
            step="1"
            onWheel={(e) => e.target.blur()}
            {...register('capacity', { 
              required: 'La capacidad es obligatoria',
              valueAsNumber: true,
              min: { value: 1, message: 'La capacidad debe ser al menos 1' }
            })}
            className={errors.capacity ? 'input-error' : ''}
            placeholder="20"
            defaultValue={initialData?.capacity || 20}
          />
          <span className="capacity-hint">máx. alumnos matriculables</span>
        </div>
        {errors.capacity && <span className="error-message">{errors.capacity.message}</span>}
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : initialData ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default GroupForm;

